import React, {useState} from "react";
import LaunchIcon from "@mui/icons-material/Launch";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PhoneIcon from "@mui/icons-material/Phone";
import PlaceIcon from "@mui/icons-material/Place";
import StreetviewIcon from "@mui/icons-material/Streetview";
import {Box, Link, Tooltip} from "@mui/material";
import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";
import {languageSelector} from "../../state/app/appSelectors";
import {hubNodesSelector} from "../../state/hub/hubSelectors";
import {nodeSelector} from "../../state/node/nodeSelectors";
import EXTRAS from "../../utils/extras";
import {formatTemporalValue, getFormattedValue} from "../../utils/formatters";
import {normalizeUrl} from "../../utils/urls";

const linkIconStyle = {
  display: "flex",
  alignItems: "center",
  color: "inherit"
};

const iconStyle = {
  marginRight: "4px",
  verticalAlign: "bottom"
};

const WEBSITE_REGEX = /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/[^\s]*)?$/;
const EMAIL_REGEX = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export const FieldWrapper = ({index, className, children}) => (
  <Box
    className={`flat-table__field-wrapper ${Boolean(index % 2) ? "flat-table__field-wrapper--odd" : "flat-table__field-wrapper--even"} ${className}`}
  >
    {children}
  </Box>
);

export const DimensionField = ({data, index}) => (
  <FieldWrapper index={index}>
    <Tooltip title={data}>
      <Box className="flat-table__field-text flat-table__field-wrapper__dimension-field">{data}</Box>
    </Tooltip>
  </FieldWrapper>
);

export const MeasureField = ({data, index, decimalPlaces, decimalSeparator, roundingStrategy}) => (
  <FieldWrapper index={index} className="flat-table__field-wrapper__measure-field">
    <span className="flat-table__field-text flat-table__field-wrapper__measure-field__value">
      {getFormattedValue(data, decimalSeparator, decimalPlaces, "", roundingStrategy, false)}
    </span>
  </FieldWrapper>
);

export const TemporalField = ({nodeId, data, index, type, formatKey, className}) => {
  const nodes = useSelector(hubNodesSelector);
  const node = useSelector(nodeSelector);
  const language = useSelector(languageSelector);

  const selectedNode = nodeId ? nodes?.find(node => node.nodeId === nodeId) : node;
  const format = EXTRAS.parse(selectedNode?.extras)?.[formatKey]?.[language];

  const formattedValue = format ? formatTemporalValue(data, type, format) : data;

  return (
    <FieldWrapper index={index} className={`flat-table__field-text ${className}`}>
      {formattedValue !== null && formattedValue !== undefined && formattedValue !== "" ? (
        <Tooltip title={formattedValue}>
          <Box className={`flat-table__field-text ${className}`}>{formattedValue}</Box>
        </Tooltip>
      ) : null}
    </FieldWrapper>
  );
};

export const DateField = ({nodeId, data, index}) => (
  <TemporalField
    data={data}
    index={index}
    nodeId={nodeId}
    type="DATE"
    formatKey="TimePeriodFormatDate"
    className="flat-table__field-wrapper__date-field"
  />
);

export const TimeField = ({nodeId, data, index}) => (
  <TemporalField
    data={data}
    index={index}
    nodeId={nodeId}
    type="TIME"
    formatKey="TimePeriodFormatTime"
    className="flat-table__field-wrapper__time-field"
  />
);

export const DayField = ({nodeId, data, index}) => (
  <TemporalField
    data={data}
    index={index}
    nodeId={nodeId}
    type="DAY"
    formatKey="TimePeriodFormatDay"
    className="flat-table__field-wrapper__day-field"
  />
);

export const MonthField = ({nodeId, data, index}) => (
  <TemporalField
    data={data}
    index={index}
    nodeId={nodeId}
    type="MONTH"
    formatKey="TimePeriodFormatMonth"
    className="flat-table__field-wrapper__month-field"
  />
);

export const YearField = ({nodeId, data, index}) => (
  <TemporalField
    data={data}
    index={index}
    nodeId={nodeId}
    type="YEAR"
    formatKey="TimePeriodFormatYear"
    className="flat-table__field-wrapper__year-field"
  />
);

export const QuarterField = ({nodeId, data, index}) => (
  <TemporalField
    data={data}
    index={index}
    nodeId={nodeId}
    type="QUARTER"
    formatKey="TimePeriodFormatQuarter"
    className="flat-table__field-wrapper__quarter-field"
  />
);

export const QuadrimesterField = ({nodeId, data, index}) => (
  <TemporalField
    data={data}
    index={index}
    nodeId={nodeId}
    type="QUADRIMESTER"
    formatKey="TimePeriodFormatQuadrimester"
    className="flat-table__field-wrapper__quadrimester-field"
  />
);

export const SemesterField = ({nodeId, data, index}) => (
  <TemporalField
    data={data}
    index={index}
    nodeId={nodeId}
    type="SEMESTER"
    formatKey="TimePeriodFormatSemester"
    className="flat-table__field-wrapper__semester-field"
  />
);

export const CoordinatesField = ({latitude, longitude, index}) => {
  const {t} = useTranslation();

  const parsedLatitude = typeof latitude === "string" ? latitude.replace(",", ".") : latitude;
  const parsedLongitude = typeof longitude === "string" ? longitude.replace(",", ".") : longitude;

  return (
    <FieldWrapper index={index} className="flat-table__field-wrapper__coordinates-field">
      {latitude !== null && latitude !== undefined && longitude !== null && longitude !== undefined && (
        <>
          <Link
            href={`https://www.google.com/maps/search/?api=1&query=${parsedLatitude},${parsedLongitude}`}
            aria-label={t("modules.flatTable.table.renderers.coordinates.googleMaps")}
            target="_blank"
            sx={linkIconStyle}
          >
            <Tooltip title={t("modules.flatTable.table.renderers.coordinates.googleMaps")}>
              <PlaceIcon
                className="flat-table__field-wrapper__icon flat-table__field-wrapper__coordinates-field__google-map-icon"
                sx={iconStyle}
                fontSize="small"
              />
            </Tooltip>
          </Link>
          <Link
            href={`https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${parsedLatitude},${parsedLongitude}`}
            aria-label={t("modules.flatTable.table.renderers.coordinates.streetView")}
            target="_blank"
            sx={linkIconStyle}
          >
            <Tooltip title={t("modules.flatTable.table.renderers.coordinates.streetView")}>
              <StreetviewIcon
                className="flat-table__field-wrapper__icon flat-table__field-wrapper__coordinates-field__street-view-icon"
                sx={iconStyle}
                fontSize="small"
              />
            </Tooltip>
          </Link>
        </>
      )}
    </FieldWrapper>
  );
};

export const ImageField = ({data, index}) => {
  const [imgError, setImgError] = useState(false);

  return (
    <FieldWrapper index={index} className="flat-table__field-text flat-table__field-wrapper__image-field">
      {data && !imgError ? (
        <a href={data} target="_blank" rel="noopener noreferrer">
          <img
            src={data}
            alt={data}
            className="flat-table__field-wrapper__image-field__img"
            onError={() => setImgError(true)}
          />
        </a>
      ) : null}
    </FieldWrapper>
  );
};

export const AddressField = ({data, index}) => {
  return (
    <FieldWrapper index={index} className="flat-table__field-wrapper__address-field">
      {data && (
        <Tooltip title={data}>
          <Box className="flat-table__field-text">
            <PlaceIcon
              className="flat-table__field-wrapper__icon flat-table__field-wrapper__address-field__icon"
              sx={iconStyle}
              fontSize="small"
            />
            <span className="flat-table__field-wrapper__address-field__text">{data}</span>
          </Box>
        </Tooltip>
      )}
    </FieldWrapper>
  );
};

export const WebsiteField = ({data, index}) => {
  const trimmedData = typeof data === "string" ? data.trim() : "";
  const isValid = WEBSITE_REGEX.test(trimmedData);
  return (
    <FieldWrapper index={index} className="flat-table__field-wrapper__website-field">
      {trimmedData && (
        <Tooltip title={trimmedData}>
          {isValid ? (
            <Link
              href={normalizeUrl(trimmedData)}
              target="_blank"
              rel="noreferrer"
              underline="hover"
              sx={linkIconStyle}
            >
              <Box className="flat-table__field-text">
                <LaunchIcon
                  className="flat-table__field-wrapper__icon flat-table__field-wrapper__website-field__icon"
                  sx={iconStyle}
                  fontSize="small"
                />
                <span className="flat-table__field-wrapper__website-field__text">{trimmedData}</span>
              </Box>
            </Link>
          ) : (
            <Box className="flat-table__field-text">
              <span className="flat-table__field-wrapper__website-field__text">{trimmedData}</span>
            </Box>
          )}
        </Tooltip>
      )}
    </FieldWrapper>
  );
};

export const PhoneField = ({data, index}) => {
  return (
    <FieldWrapper index={index} className="flat-table__field-wrapper__phone-field">
      {data && (
        <Tooltip title={data}>
          <Link href={`tel:${data}`} underline="hover" sx={linkIconStyle}>
            <Box className="flat-table__field-text">
              <PhoneIcon
                className="flat-table__field-wrapper__icon flat-table__field-wrapper__phone-field__icon"
                sx={iconStyle}
                fontSize="small"
              />
              <span className="flat-table__field-wrapper__phone-field__text">{data}</span>
            </Box>
          </Link>
        </Tooltip>
      )}
    </FieldWrapper>
  );
};

export const MailField = ({data, index}) => {
  const trimmedData = typeof data === "string" ? data.trim() : "";
  const isValid = EMAIL_REGEX.test(trimmedData);
  return (
    <FieldWrapper index={index} className="flat-table__field-wrapper__mail-field">
      {trimmedData && (
        <Tooltip title={trimmedData}>
          {isValid ? (
            <Link href={`mailto:${trimmedData}`} underline="hover" sx={linkIconStyle}>
              <Box className="flat-table__field-text">
                <MailOutlineIcon
                  className="flat-table__field-wrapper__icon flat-table__field-wrapper__mail-field__icon"
                  sx={iconStyle}
                  fontSize="small"
                />
                <span className="flat-table__field-wrapper__mail-field__text">{trimmedData}</span>
              </Box>
            </Link>
          ) : (
            <Box className="flat-table__field-text">
              <span className="flat-table__field-wrapper__mail-field__text">{trimmedData}</span>
            </Box>
          )}
        </Tooltip>
      )}
    </FieldWrapper>
  );
};
